import { datadogLogger } from '@fanduel/casino-datadog-logger';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';

import { ERROR_TYPE } from '@/features/Popup/constants';
import { useStore } from '@/store/useStore';
import { areUserSessionTokensReadyForGame } from '@/utilities/helpers/areUserSessionTokensReadyForGame';

import { buildTelemetryContext } from '../telemetry-context';

import {
  VITAL_NAMES,
  clearVitalsStorage as clearStoredVitals,
  getLaunchAttempt,
  getVitalsGameUid,
  hasVitalCheckpoint,
  logVitalsTable,
  recordVitalCheckpoint,
  removeVitalFromStorage,
  resolveDatadogVitalName,
  rotateVitalsLaunchAttempt,
  type VitalName,
} from './vitalsStorage';

/** Error details attached to vitals stopped by the error-modal guard. */
export type VitalsErrorContext = {
  errorType?: string;
  errorCode?: string;
  errorMessage?: string;
};

type PendingVital = {
  checkpoint: VitalDurationType;
  context: Record<string, unknown>;
  name: string;
};

let loggerReady = false;
let pendingVitals: PendingVital[] = [];
type ActiveVitalEntry = {
  context: Record<string, unknown>;
  datadogName: string;
};
const activeVitals = new Map<string, ActiveVitalEntry>();
let vitalsGeneration = 0;
/** Set by full relaunch reset; FD/GAME_LAUNCH START on first fetch/GLS START (handshake fallback). */
let mountVitalsStartPending = false;
type JackpotReconciliationState =
  | 'idle'
  | 'candidate'
  | 'prewarm'
  | 'relaunching';
let jackpotReconciliationState: JackpotReconciliationState = 'idle';
let contextReceivedWhileReconciliationPending = false;
let gameReadyWhileReconciliationPending = false;
/** CONTEXT arrived before GLS STOP (stale or ordering); close FD after GLS completes. */
let contextReceivedAwaitingGlsStop = false;
/** Iframe DOM `onLoad` fired for the current reload cycle (logout may load before session). */
let gameClientIframeDomReady = false;

const isSessionReadyForGameClientVital = (): boolean => {
  const state = useStore.getState();

  if (state.gameError?.type === ERROR_TYPE.SESSION_EXPIRED) {
    return false;
  }

  const wrapper = state.runtime?.wrapperType;

  return areUserSessionTokensReadyForGame(
    state.userSessionContext,
    wrapper?.isAndroid || wrapper?.isXsell,
  );
};

/** Vitals that belong to one launch attempt (iframe load, GLS, provider load). */
const LAUNCH_ATTEMPT_VITALS: VitalName[] = [
  VITAL_NAMES.GAME_CLIENT,
  VITAL_NAMES.GAME_LOAD,
  VITAL_NAMES.GLS,
  VITAL_NAMES.GAME_FETCH,
  VITAL_NAMES.GEOLOCATION,
];

const LAUNCH_ENVELOPE_VITALS: VitalName[] = [
  VITAL_NAMES.FD_LAUNCH,
  VITAL_NAMES.GAME_LAUNCH,
];

const resetReconciliationState = () => {
  jackpotReconciliationState = 'idle';
  contextReceivedWhileReconciliationPending = false;
  gameReadyWhileReconciliationPending = false;
  contextReceivedAwaitingGlsStop = false;
};

const hasGlsStopForCurrentAttempt = (): boolean =>
  hasVitalCheckpoint(VITAL_NAMES.GLS, VitalDurationType.STOP);

const emitVital = ({ checkpoint, context, name }: PendingVital) => {
  datadogLogger.ddRumVitalDuration(checkpoint, {
    name,
    description: `${name} ${checkpoint}`,
    context,
    type: checkpoint,
  });
};

const submitVital = (vital: PendingVital) => {
  if (loggerReady) {
    emitVital(vital);
  } else {
    pendingVitals.push(vital);
  }
};

export const markVitalsLoggerReady = () => {
  if (loggerReady) return;

  loggerReady = true;
  const queuedVitals = pendingVitals;
  pendingVitals = [];
  queuedVitals.forEach(emitVital);
};

export const __resetVitalsLoggerForTests = () => {
  loggerReady = false;
  pendingVitals = [];
  activeVitals.clear();
  vitalsGeneration = 0;
  mountVitalsStartPending = false;
  gameClientIframeDomReady = false;
  resetReconciliationState();
};

/** Cleared before iframe reload so a stale onLoad flag cannot start vitals early. */
export const resetGameClientIframeDomReadyForReload = (): void => {
  gameClientIframeDomReady = false;
};

/**
 * Records iframe DOM load and starts GAME_CLIENT when session is ready (logout Try
 * again loads the iframe before login; START is deferred until re-auth).
 */
export const markGameClientIframeDomReadyForVitals = (): void => {
  gameClientIframeDomReady = true;
  maybeStartGameClientDurationVital();
};

/** Starts GAME_CLIENT once per attempt when iframe has loaded and session allows launch. */
export const maybeStartGameClientDurationVital = (): void => {
  if (!gameClientIframeDomReady) {
    return;
  }

  if (!isSessionReadyForGameClientVital()) {
    return;
  }

  if (useStore.getState().gameStatus.jackpotReconcileRunning) {
    return;
  }

  const gameUid = getVitalsGameUid();

  if (
    hasVitalCheckpoint(
      VITAL_NAMES.GAME_CLIENT,
      VitalDurationType.START,
      gameUid,
    )
  ) {
    return;
  }

  if (activeVitals.has(VITAL_NAMES.GAME_CLIENT)) {
    return;
  }

  processGameClientDurationVital(VitalDurationType.START);
};

const canCloseLaunchVitalsFromGameClient = (): boolean =>
  useStore.getState().clientStatus.loaded;

/**
 * Records and emits each checkpoint at most once. Checkpoints raised before
 * Datadog initialization are queued so the SDK cannot silently drop them.
 */
const processVital = (
  name: string,
  checkpoint: VitalDurationType,
  expectedGeneration?: number,
): number => {
  if (expectedGeneration != null && expectedGeneration !== vitalsGeneration) {
    return vitalsGeneration;
  }

  const gameUid = getVitalsGameUid();
  const recorded = recordVitalCheckpoint(name, checkpoint, gameUid);
  if (recorded === false) return vitalsGeneration;

  const active = activeVitals.get(name);
  const datadogName =
    checkpoint === VitalDurationType.STOP && active
      ? active.datadogName
      : resolveDatadogVitalName(name);
  const context = buildTelemetryContext();
  const vitalContext = {
    ...context,
    gameUid: context.gameUid ?? gameUid,
    launchAttempt: getLaunchAttempt(),
  };
  const vital = {
    checkpoint,
    context:
      checkpoint === VitalDurationType.STOP && active
        ? active.context
        : vitalContext,
    name: datadogName,
  };

  if (checkpoint === VitalDurationType.START) {
    activeVitals.set(name, { context: vitalContext, datadogName });
  } else if (active) {
    activeVitals.delete(name);
  }
  submitVital(vital);

  if (
    name === VITAL_NAMES.GAME_LAUNCH &&
    checkpoint === VitalDurationType.STOP
  ) {
    logVitalsTable(gameUid);
  }

  return vitalsGeneration;
};

/**
 * Stops every in-flight Datadog vital before clearing local checkpoints.
 * This prevents a soft reload or identity reset from starting a second vital
 * with the same name while the previous SDK vital is still active.
 */
export const clearVitalsStorage = (gameUid?: string) => {
  activeVitals.forEach(({ context, datadogName }) =>
    submitVital({
      checkpoint: VitalDurationType.STOP,
      context,
      name: datadogName,
    }),
  );
  activeVitals.clear();
  vitalsGeneration += 1;
  resetReconciliationState();
  clearStoredVitals(gameUid);
};

const stopTrackedVitalsWithError = (
  vitalNames: VitalName[],
  error: VitalsErrorContext,
) => {
  const gameUid = getVitalsGameUid();

  for (const name of vitalNames) {
    const active = activeVitals.get(name);
    if (!active) {
      continue;
    }

    recordVitalCheckpoint(name, VitalDurationType.STOP, gameUid);
    submitVital({
      checkpoint: VitalDurationType.STOP,
      context: { ...active.context, vitalError: error, aborted: true },
      name: active.datadogName,
    });
    activeVitals.delete(name);
  }
};

const stopAndClearVitalForSoftReload = (
  name: VitalName,
  gameUid: string,
): void => {
  const active = activeVitals.get(name);
  if (active) {
    recordVitalCheckpoint(name, VitalDurationType.STOP, gameUid);
    submitVital({
      checkpoint: VitalDurationType.STOP,
      context: active.context,
      name: active.datadogName,
    });
    activeVitals.delete(name);
  }
  removeVitalFromStorage(name, gameUid);
};

/**
 * Ends the current launch attempt in Datadog, clears stored checkpoints (console
 * table), and allows Try again to START fresh FD / game-client / game-launch /
 * GLS / game-load vitals. Error modals still keep the envelope open until this runs.
 */
export const resetLaunchAttemptVitalsForSoftReload = () => {
  const gameUid = getVitalsGameUid();

  for (const name of [...LAUNCH_ATTEMPT_VITALS, ...LAUNCH_ENVELOPE_VITALS]) {
    stopAndClearVitalForSoftReload(name, gameUid);
  }

  vitalsGeneration += 1;
  resetReconciliationState();
  logVitalsTable(gameUid);
};

/**
 * Guard for hung launch vitals: when a provider/GLS error modal or maintenance
 * is shown before GameReady, stop every in-flight vital (including FD and game
 * launch) with {@link VitalsErrorContext} so dashboards can filter on
 * `vitalError` / `aborted`. Try again uses
 * {@link resetFullLaunchVitalsForSoftReload} to START fresh spans. No-op
 * once GAME_LAUNCH has completed (GameReady received).
 */
export const stopVitalsOnError = (error: VitalsErrorContext = {}) => {
  const gameUid = getVitalsGameUid();

  if (
    hasVitalCheckpoint(VITAL_NAMES.GAME_LAUNCH, VitalDurationType.STOP, gameUid)
  ) {
    return;
  }

  const inFlightNames = [...activeVitals.keys()] as VitalName[];
  stopTrackedVitalsWithError(inFlightNames, error);

  for (const name of [...LAUNCH_ATTEMPT_VITALS, ...LAUNCH_ENVELOPE_VITALS]) {
    removeVitalFromStorage(name, gameUid);
  }

  vitalsGeneration += 1;
  resetReconciliationState();
  logVitalsTable(gameUid);
};

/**
 * Full relaunch vitals reset (Try again, jackpot opt-out).
 *
 * 1. Launch still in flight (no `GAME_LAUNCH` STOP): abort with {@link VitalsErrorContext}
 *    so the attempt is excluded from success metrics (`vitalError` / `aborted`).
 * 2. Game already loaded (`GAME_LAUNCH` STOP recorded): clear attempt/envelope
 *    checkpoints only — no error abort — then {@link restartFullLaunchMountVitals}
 *    STARTs a new launch.
 */
export const resetFullLaunchVitalsForSoftReload = (
  error: VitalsErrorContext = {},
) => {
  const gameUid = getVitalsGameUid();
  const launchComplete = hasVitalCheckpoint(
    VITAL_NAMES.GAME_LAUNCH,
    VitalDurationType.STOP,
    gameUid,
  );

  if (launchComplete) {
    for (const name of LAUNCH_ATTEMPT_VITALS) {
      stopAndClearVitalForSoftReload(name, gameUid);
    }
    vitalsGeneration += 1;
    resetReconciliationState();
    logVitalsTable(gameUid);
  } else {
    abortLaunchVitalsForRetry(error);
  }

  rotateVitalsLaunchAttempt();
};

/** Error-stop the full launch envelope and wipe storage (e.g. continue without jackpots). */
export const abortLaunchVitalsForRetry = (error: VitalsErrorContext = {}) => {
  const gameUid = getVitalsGameUid();

  if (
    hasVitalCheckpoint(VITAL_NAMES.GAME_LAUNCH, VitalDurationType.STOP, gameUid)
  ) {
    return;
  }

  stopTrackedVitalsWithError(
    [...LAUNCH_ATTEMPT_VITALS, ...LAUNCH_ENVELOPE_VITALS],
    error,
  );
  vitalsGeneration += 1;
  resetReconciliationState();
  clearStoredVitals();
  logVitalsTable(gameUid);
};

export const processFdLaunchDurationVital = (checkpoint: VitalDurationType) =>
  processVital(VITAL_NAMES.FD_LAUNCH, checkpoint);

export const processGameClientDurationVital = (checkpoint: VitalDurationType) =>
  processVital(VITAL_NAMES.GAME_CLIENT, checkpoint);

export const processLoadDurationVital = (checkpoint: VitalDurationType) =>
  processVital(VITAL_NAMES.GAME_LOAD, checkpoint);

export const processGameLaunchDurationVital = (checkpoint: VitalDurationType) =>
  processVital(VITAL_NAMES.GAME_LAUNCH, checkpoint);

const maybeStartPendingMountVitals = (): void => {
  if (!mountVitalsStartPending) {
    return;
  }

  mountVitalsStartPending = false;
  startMountVitals();
};

export const processFetchDurationVital = (
  checkpoint: VitalDurationType,
  generation?: number,
): number => {
  const nextGeneration = processVital(
    VITAL_NAMES.GAME_FETCH,
    checkpoint,
    generation,
  );

  if (checkpoint === VitalDurationType.START) {
    maybeStartPendingMountVitals();
  }

  return nextGeneration;
};

export const processGeolocationDurationVital = (
  checkpoint: VitalDurationType,
) => processVital(VITAL_NAMES.GEOLOCATION, checkpoint);

export const processGlsDurationVital = (checkpoint: VitalDurationType) => {
  processVital(VITAL_NAMES.GLS, checkpoint);

  if (checkpoint === VitalDurationType.START) {
    maybeStartPendingMountVitals();
  }

  if (checkpoint === VitalDurationType.STOP) {
    flushDeferredContextReceivedVitals();
  }
};

const completeContextReceivedVitals = () => {
  processGameClientDurationVital(VitalDurationType.STOP);
  processFdLaunchDurationVital(VitalDurationType.STOP);
  processLoadDurationVital(VitalDurationType.START);
};

const tryCompleteContextReceivedVitals = (): void => {
  if (!canCloseLaunchVitalsFromGameClient()) {
    return;
  }

  if (!activeVitals.has(VITAL_NAMES.FD_LAUNCH)) {
    return;
  }

  if (!hasGlsStopForCurrentAttempt()) {
    return;
  }

  completeContextReceivedVitals();
};

const flushDeferredContextReceivedVitals = (): void => {
  if (!contextReceivedAwaitingGlsStop) {
    return;
  }

  contextReceivedAwaitingGlsStop = false;
  tryCompleteContextReceivedVitals();
};

const completeGameReadyVitals = () => {
  processLoadDurationVital(VitalDurationType.STOP);
  processGameLaunchDurationVital(VitalDurationType.STOP);
};

const tryCompleteGameReadyVitals = (): void => {
  if (!canCloseLaunchVitalsFromGameClient()) {
    return;
  }

  if (!activeVitals.has(VITAL_NAMES.GAME_LAUNCH)) {
    return;
  }

  if (!activeVitals.has(VITAL_NAMES.GAME_LOAD)) {
    return;
  }

  completeGameReadyVitals();
};

/** Defers GC launch checkpoints while jackpot reconciliation is unresolved. */
export const markLaunchVitalsReconciliationPending = () => {
  jackpotReconciliationState = 'candidate';
};

/**
 * Holds GL/INITIALIZE_URL until aggregation settles and GW knows whether a
 * jackpot GLS relaunch is required (avoids the game-client applying a stale URL).
 */
export const isJackpotGlsUrlDeliveryDeferred = (): boolean =>
  jackpotReconciliationState === 'candidate';

/** Aggregation settled: allow delivering the prewarm GLS URL once before recon. */
export const markJackpotReconPrewarmDeliveryReady = (): void => {
  if (jackpotReconciliationState === 'candidate') {
    jackpotReconciliationState = 'prewarm';
  }
};

/** True while the prewarm GL/INITIALIZE_URL awaits GC/CONTEXT_RECEIVED. */
export const isJackpotReconPrewarmAwaitingContext = (): boolean =>
  jackpotReconciliationState === 'prewarm';

/** Allows only events from the reconciled GLS attempt to close launch vitals. */
export const markLaunchVitalsReconciledAttemptReady = () => {
  if (jackpotReconciliationState !== 'relaunching') {
    return;
  }

  resetReconciliationState();
};

const shouldDeferLaunchVitalsFromGameClient = (): boolean => {
  if (useStore.getState().gameStatus.jackpotReconcileRunning) {
    return true;
  }

  return (
    jackpotReconciliationState === 'candidate' ||
    jackpotReconciliationState === 'relaunching'
  );
};

export const processContextReceivedVitals = () => {
  if (shouldDeferLaunchVitalsFromGameClient()) {
    contextReceivedWhileReconciliationPending = true;
    return;
  }

  if (!canCloseLaunchVitalsFromGameClient()) {
    return;
  }

  if (!activeVitals.has(VITAL_NAMES.FD_LAUNCH)) {
    return;
  }

  if (!hasGlsStopForCurrentAttempt()) {
    contextReceivedAwaitingGlsStop = true;
    return;
  }

  completeContextReceivedVitals();
};

export const processGameReadyVitals = () => {
  if (shouldDeferLaunchVitalsFromGameClient()) {
    gameReadyWhileReconciliationPending = true;
    return;
  }

  if (!canCloseLaunchVitalsFromGameClient()) {
    return;
  }

  tryCompleteGameReadyVitals();
};

/**
 * Resolves a possible jackpot reconciliation without blocking provider prewarm.
 * A relaunch discards checkpoints from the prewarm attempt; otherwise deferred
 * checkpoints are flushed in their original order.
 */
export const resolveLaunchVitalsReconciliation = (willRelaunch: boolean) => {
  if (willRelaunch) {
    jackpotReconciliationState = 'relaunching';
    contextReceivedWhileReconciliationPending = false;
    gameReadyWhileReconciliationPending = false;
    return;
  }

  jackpotReconciliationState = 'idle';

  if (contextReceivedWhileReconciliationPending) {
    tryCompleteContextReceivedVitals();
  }
  if (gameReadyWhileReconciliationPending) {
    tryCompleteGameReadyVitals();
  }

  contextReceivedWhileReconciliationPending = false;
  gameReadyWhileReconciliationPending = false;
};

/** Starts FD launch and game-launch vitals when launch timing begins (valid session). */
export const startMountVitals = () => {
  processFdLaunchDurationVital(VitalDurationType.START);
  processGameLaunchDurationVital(VitalDurationType.START);
};

/** Drops stored + in-memory mount vitals so a relaunch can record fresh START checkpoints. */
const clearMountVitalCheckpoints = (gameUid: string = getVitalsGameUid()) => {
  for (const name of [...LAUNCH_ENVELOPE_VITALS, VITAL_NAMES.GAME_CLIENT]) {
    activeVitals.delete(name);
    removeVitalFromStorage(name, gameUid);
  }
};

export type SoftReloadMountVital = 'fdLaunch' | 'gameClient' | 'gameLaunch';

/**
 * Clears envelope/client checkpoints before iframe reload. START is deferred to
 * {@link startMountVitalsAfterClientIframeLoad} so stale GC events cannot close
 * vitals in the gap before the new iframe loads.
 */
export const restartFullLaunchMountVitals = (): void => {
  clearMountVitalCheckpoints();
  mountVitalsStartPending = true;
};

/** Fallback when fetch/GLS have not started yet (e.g. handshake before launch I/O). */
export const startMountVitalsAfterClientIframeLoad = (): void => {
  maybeStartPendingMountVitals();
};

/** Restarts only the mount vitals implicated by a soft-reload preset. */
export const restartSoftReloadVitals = (vitals: SoftReloadMountVital[]) => {
  for (const vital of vitals) {
    switch (vital) {
      case 'fdLaunch':
        processFdLaunchDurationVital(VitalDurationType.START);
        break;
      case 'gameClient':
        processGameClientDurationVital(VitalDurationType.START);
        break;
      case 'gameLaunch':
        processGameLaunchDurationVital(VitalDurationType.START);
        break;
      default:
        break;
    }
  }
};
